/**
 * MODULE: _shared/frontend-shell/src/layout/shellAssistant/ShellContentWithAssistant.tsx
 * PURPOSE: Main content + Perplexity-style right assistant rail (in-flow split on desktop).
 * PUBLIC_API: ShellContentWithAssistant
 * AGENT_SAFETY: SAFE
 * RECENT: 2026-06-18 [2.2.1450] — Right rail in-flow split; **`/langflow-assistant`** uses full-page panel via **`showAssistantChrome={false}`**; mobile bottom bar when split active.
 */

import type { ReactNode } from 'react';
import { MobileBottomNav } from '../../components/nav/MobileBottomNav';
import { MobilePageScope } from '../mobile/MobilePageScope';
import { AIAssistantRightRail } from './AIAssistantRightRail';
import { ShellAssistantBottomBar } from './ShellAssistantBottomBar';
import { useShellAssistant } from './ShellAssistantContext';

export type ShellContentWithAssistantProps = {
  children: ReactNode;
  pageContext: { path: string; title?: string };
  pageTitle?: string;
  showAssistantChrome: boolean;
  showMobileBottomNav: boolean;
  isMobileShell?: boolean;
  mainClassName: string;
  contentKey: string;
  salesHubSubShellActive: boolean;
  crmSubShellActive: boolean;
  calendarMainScrollActive: boolean;
};

export function ShellContentWithAssistant({
  children,
  pageContext,
  pageTitle,
  showAssistantChrome,
  showMobileBottomNav,
  isMobileShell = false,
  mainClassName,
  contentKey,
  salesHubSubShellActive,
  crmSubShellActive,
  calendarMainScrollActive,
}: ShellContentWithAssistantProps) {
  const { session } = useShellAssistant();
  const assistantSplit =
    showAssistantChrome && session.dockExpanded && session.panelOpen;

  return (
    <div
      className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden"
      data-shell-assistant-split={assistantSplit ? 'true' : 'false'}
    >
      <div className="flex min-h-0 min-w-0 flex-1 flex-row overflow-hidden">
        <main
          id="main-content"
          role="main"
          data-shell-sales-hub-sub={salesHubSubShellActive ? 'true' : 'false'}
          data-shell-crm-sub={crmSubShellActive ? 'true' : 'false'}
          data-shell-calendar-scroll={calendarMainScrollActive ? 'true' : 'false'}
          data-shell-assistant-split={assistantSplit ? 'true' : 'false'}
          data-mobile-shell={isMobileShell ? 'true' : 'false'}
          className={mainClassName}
        >
          <div key={contentKey} className="flex min-h-0 min-w-0 flex-1 flex-col">
            <MobilePageScope isMobileShell={isMobileShell}>{children}</MobilePageScope>
          </div>
        </main>
        {showAssistantChrome ? <AIAssistantRightRail pageContext={pageContext} /> : null}
      </div>
      {assistantSplit && isMobileShell ? (
        <ShellAssistantBottomBar pageTitle={pageTitle} />
      ) : null}
      {showMobileBottomNav && !assistantSplit ? <MobileBottomNav embedded /> : null}
    </div>
  );
}
